import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  BookOpen,
  BookMarked,
  HandHeart,
  Sparkles,
  MoreHorizontal,
  Compass,
  HelpCircle,
  X,
} from "lucide-react";
import BottomSheet from "@/components/BottomSheet";

const NAV_ITEMS = [
  {
    id: "surah",
    label: "Surah",
    icon: BookOpen,
    path: "/",
    exact: true,
  },
  {
    id: "juz",
    label: "Juz",
    icon: BookMarked,
    path: "/juz",
  },
  {
    id: "doa",
    label: "Doa",
    icon: HandHeart,
    path: "/doa",
  },
  {
    id: "ai",
    label: "Tanya AI",
    icon: Sparkles,
    path: "/tanya-ai",
  },
];

const MORE_ITEMS = [
  {
    id: "qiblat",
    label: "Arah Qiblat",
    icon: Compass,
    path: "/qiblat",
    desc: "Temukan arah kiblat dengan kompas digital",
  },
  {
    id: "bantuan",
    label: "Bantuan",
    icon: HelpCircle,
    path: "/bantuan",
    desc: "Panduan penggunaan Al-Qur'an Indonesia",
  },
];

/**
 * BottomNavigation — Tab bar navigasi utama di bagian bawah layar.
 * Hanya tampil di mobile (md:hidden).
 */
export default function BottomNavigation() {
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Jika scroll ke atas atau berada di posisi paling atas layar (< 50px)
      if (currentScrollY < lastScrollY || currentScrollY < 50) {
        setIsVisible(true);
      } 
      // Jika scroll ke bawah lebih dari 50px (lagi asyik membaca)
      else if (currentScrollY > 50 && currentScrollY > lastScrollY) {
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const isActive = (item) => {
    if (item.exact) return pathname === item.path;
    return pathname === item.path || pathname.startsWith(item.path + "/");
  };

  return (
    <>
      <nav
        className={`fixed left-4 right-4 z-50 md:hidden mb-safe transition-transform duration-500 ease-in-out ${
          isVisible ? "translate-y-0" : "translate-y-[150%]"
        }`}
        style={{ bottom: "1rem" }}
        aria-label="Navigasi utama"
      >
        {/* Floating Blur glass background */}
        <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-2xl border border-white/40 dark:border-slate-700/50 shadow-[0_8px_30px_rgb(0,0,0,0.12)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.3)] rounded-[2rem] overflow-hidden">
          <div className="flex items-stretch h-16">
            {NAV_ITEMS.map((item) => {
              const active = isActive(item);
              const Icon = item.icon;
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  aria-label={item.label}
                  className={`
                    flex-1 flex flex-col items-center justify-center gap-1 pt-1 pb-1
                    transition-all duration-300 active:scale-90 relative
                    ${active ? "text-emerald-600 dark:text-emerald-400" : "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"}
                  `}
                >
                  {/* Active indicator dot */}
                  {active && (
                    <span className="absolute top-0 left-1/2 -ml-3 w-6 h-1 bg-emerald-500 rounded-b-full tab-active-pop" />
                  )}

                  <div className={`transition-transform duration-300 ${active ? "-translate-y-1" : "translate-y-0"}`}>
                    <Icon
                      size={22}
                      strokeWidth={active ? 2.5 : 2}
                      className={active ? "drop-shadow-sm" : ""}
                    />
                  </div>
                  <span
                    className={`text-[10px] font-bold leading-none transition-all duration-300 absolute bottom-2 ${
                      active ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
                    }`}
                  >
                    {item.label}
                  </span>
                </Link>
              );
            })}

            {/* Tab Lainnya */}
            <button
              onClick={() => setMoreOpen(true)}
              aria-label="Lainnya"
              className={`
                flex-1 flex flex-col items-center justify-center gap-0.5 pt-2 pb-1
                transition-all duration-200 active:scale-90 relative
                ${moreOpen ? "text-emerald-600 dark:text-emerald-400" : "text-gray-400 dark:text-gray-500"}
              `}
            >
              <MoreHorizontal size={20} strokeWidth={1.8} />
              <span className="text-[10px] font-bold leading-none">Lainnya</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Bottom Sheet — Lainnya */}
      <BottomSheet
        isOpen={moreOpen}
        onClose={() => setMoreOpen(false)}
        title="Lainnya"
      >
        <div className="p-4 space-y-2">
          {MORE_ITEMS.map((item) => {
            const Icon = item.icon;
            const active =
              pathname === item.path || pathname.startsWith(item.path + "/");
            return (
              <Link
                key={item.id}
                to={item.path}
                onClick={() => setMoreOpen(false)}
                className={`
                  flex items-center gap-4 p-4 rounded-2xl transition-all active:scale-[0.98]
                  ${
                    active
                      ? "bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800"
                      : "bg-gray-50 dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700"
                  }
                `}
              >
                <div
                  className={`
                  w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0
                  ${
                    active
                      ? "bg-emerald-500 text-white"
                      : "bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300"
                  }
                `}
                >
                  <Icon size={20} strokeWidth={1.8} />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className={`font-bold text-sm ${
                      active
                        ? "text-emerald-700 dark:text-emerald-400"
                        : "text-gray-900 dark:text-white"
                    }`}
                  >
                    {item.label}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                    {item.desc}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </BottomSheet>
    </>
  );
}
